import React, { useEffect, useCallback, useState, useMemo } from 'react';
import axios from 'axios';
import { Group, Paginator as ModelPaginator } from '../model';
import { GroupNameWithColor } from '../ui/groupNameWithColor';
import { CheckboxWithImage, Loading, MenuInRow, Modal, MultiSelectForStatus, MultiSelectState, Paginator, SearchResource, SortResource } from '../ui';
import { Link, useHistory } from 'react-router-dom';
import { apiBaseUrl, DefaultPageSize } from "../Consts";
import { Empty } from '../empty';
import { GroupEdit } from '../group-edit';
import { Status } from '../status';
import { RemoveButton } from '../images';
import { useQuery } from '../hooks/UseQuery';
import { Trans, useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { teamState, isOwnerOrMemberState, lastMonitorsUpdatedState } from '../atoms';
import { useAdjustPage, useHandleRequestError } from '../hooks';
import { GroupSelectors } from './GroupsSelectors';

export const Groups: React.FC = () => {

  const { t } = useTranslation();
  const { handleRequestError } = useHandleRequestError();
  const history = useHistory();
  const query = useQuery();
  const team = useRecoilValue(teamState);
  const isOwnerOrMember = useRecoilValue(isOwnerOrMemberState);
  const lastMonitorsUpdated = useRecoilValue(lastMonitorsUpdatedState);
  const baseUrl = useMemo(() => team ? `/${team.slug}/groups` : undefined, [team]);

  const [ selectors, setSelectors ] = useState<GroupSelectors>()
  const [ editingSelector, setEditingSelector ] = useState<Group>()
  const [ removingGroup, setRemovingSelector ] = useState<Group>()
  const [ paginator, setPaginator ] = useState<ModelPaginator>();

  const [ isSelectingForRemove, setIsSelectingForRemove ] = useState<boolean>(false);

  const queryText = useMemo(() => query.get("text") ?? undefined, [query]);
  const queryOrder = useMemo(() => query.get("order") ?? undefined, [query]);
  const queryDescending = useMemo(() => query.get("descending") ? query.get("descending") === "true" : false, [query]);
  const [updatingQuery, setUpdatingQuery] = useState(false);
  const { adjustPage, shouldFetchAndReset } = useAdjustPage(baseUrl);

  const fetch = useCallback(() => {
    if (team) {
      axios.get(`${apiBaseUrl}/${team.uuid}/groups`, {params: {
        text: query.get("text"),
        order: query.get("order"),
        descending: query.get("descending"),
        recordOffset: query.get("page") ?
          (parseInt(query.get("page") as string) - 1) * DefaultPageSize : 0
      }})
        .then(response => {
          setSelectors(new GroupSelectors(response.data.groups as Group[]));
          setPaginator(response.data.paginator);
          adjustPage(response.data.paginator as ModelPaginator);
        })
        .catch(err => handleRequestError(err))
        .finally(() => setUpdatingQuery(false));
    }
  }, [team, query, adjustPage, handleRequestError]);

  const onUpdate = useCallback((group: Group) => {
    if (selectors) {
      setSelectors(selectors.updateFromRemote([group]));
    }
    setEditingSelector(undefined);
  }, [selectors]);

  const execRemove = useCallback(() => {
    if (team && removingGroup) {
      setRemovingSelector(undefined)
      axios.delete(`${apiBaseUrl}/${team.uuid}/groups/${removingGroup.uuid}`,
        {
          params: {
            text: query.get("text"),
            order: query.get("order"),
            descending: query.get("descending"),
            recordOffset: query.get("page") ?
              (parseInt(query.get("page") as string) - 1) * DefaultPageSize : 0
          }
        })
        .then(response => {
          setSelectors(new GroupSelectors(response.data.groups as Group[]));
          setPaginator(response.data.paginator);
          adjustPage(response.data.paginator as ModelPaginator);
        })
        .catch(err => handleRequestError(err));
        setRemovingSelector(undefined);
      }
  }, [team, removingGroup, query, adjustPage, handleRequestError]);

  const removeGroups = useCallback(() => {
    if (team && selectors) {
      const uuids = selectors.list.filter(gs => gs.selected).map(gs => gs.group.uuid);
      axios.delete(`${apiBaseUrl}/${team.uuid}/groups`,
        {
          params: {
            text: query.get("text"),
            order: query.get("order"),
            descending: query.get("descending"),
            recordOffset: query.get("page") ?
              (parseInt(query.get("page") as string) - 1) * DefaultPageSize : 0,
            uuids: uuids
          }
        })
        .then(response => {
          setSelectors(new GroupSelectors(response.data.groups as Group[]));
          setPaginator(response.data.paginator);
          adjustPage(response.data.paginator as ModelPaginator);
        })
        .catch(err => handleRequestError(err));
      }
      setIsSelectingForRemove(false);
    }, [team, selectors, query, adjustPage, handleRequestError]);

  const changeQuery = useCallback((queryText?: string, queryOrder?: string, queryDescending?: boolean) => {
    if (baseUrl) {
      setUpdatingQuery(true);

      const _query = new URLSearchParams(query);
      if (queryText) {
        _query.set("text", queryText);
      } else {
        _query.delete("text");
        if (queryOrder) {
          _query.set("order", queryOrder);
        } else {
          _query.delete("order");
        }
        if (queryDescending) {
          _query.set("descending", queryDescending.toString());
        } else {
          _query.delete("descending");
        }
      }
      history.push(`${baseUrl}?${_query.toString()}`);
    }
  }, [baseUrl, history, query]);

  const onCancelEdit = useCallback(() => setEditingSelector(undefined), [])

  useEffect(() => {
    if (shouldFetchAndReset()) {
      fetch();
    }
  }, [fetch, shouldFetchAndReset]);


  useEffect(() => {
    if (lastMonitorsUpdated) {
      setSelectors(selectors => selectors?.updateMonitorsFromRemote(lastMonitorsUpdated.map(mse => mse.monitor)));
    }
  }, [lastMonitorsUpdated]);

  if (!selectors || !team || !paginator) {

    return <Loading />

  } else if (selectors.length === 0 && !queryText && !updatingQuery) {

    return (
      <Empty
        description={
          <span>
            <Trans t={t} i18nKey="No groups <1/> added yet">
              No groups<br/>added yet
            </Trans>
          </span>}
        actionTitle={t("Add a group")}
        onAction={() => history.push(`/${team.slug}/groups/new`)} />
    )

  } else {
    return (
      <>
        <div className="d-f fb-d-c m-h-a m-b-1 m-b-2-M p-h-0.5 p-h-2-M w-100% max-w-66-L t-c-nb">
          <div className="d-f p-l-0.5 p-v-1.5 m-t-0.5 fb-d-cr fb-d-r-L a-i-c-L">
            <div className="d-n d-f-L f-s-2 f-w-300 fb-n">
              {t("Groups")}
            </div>
            <div className="fb-a w-100% p-h-2-L m-t-2 m-t-0-L">
              <SearchResource
                initValue={queryText}
                onChange={(value) => value !== queryText ? changeQuery(value) : null} />
            </div>
            <div className="d-f a-i-c fb-n">
              <div className="d-n-L f-s-1.5 f-w-300 fb-a">
                {t("Groups")}
              </div>
              <div className="fb-n">
                <SortResource
                  options={{
                    "name": t("Name"),
                    "lastCreated": t("Last created"),
                    "lastModified": t("Last modified"),
                  }}
                  desc={queryDescending}
                  order={queryOrder ?? "name"}
                  disabled={queryText !== undefined}
                  onChange={(order: string, desc: boolean) => { changeQuery(undefined, order, desc) }} />
              </div>
            </div>
          </div>
          {selectors.length === 0 ?
            <div className="d-f h-5 a-i-c f-s-1 p-l-0.5">
              {t("No result. Try your search again with more specific keywords.")}
            </div>
          :
            <>
              <div className="d-f h-5 a-i-c b-sb-s b-c-lightestGray b-wb-0.1 f-w-b">
                <div className="d-f p-l-0.5 fb-a">
                  <div className="d-f fb-n w-3 a-i-c">
                    {isOwnerOrMember ?
                      <MultiSelectForStatus
                        onChange={() => setSelectors(selectors.changeMultiSelectState())}
                        state={selectors.multiSelectState} />
                      : null
                    }
                  </div>
                  <div className="fb-a d-f a-i-c m-r-1 t-c-b">
                    {selectors.multiSelectState === MultiSelectState.None ?
                        t("Group")
                      :
                      <div onClick={() => setIsSelectingForRemove(true)} title={t("Delete")} className="d-f c-p t-c-nb">
                        <RemoveButton />
                      </div>
                    }
                  </div>
                </div>
                <div className="d-n d-f-M w-20-M w-25-L">{t("Monitors")}</div>
                {isOwnerOrMember ?
                  <div className="d-f w-2"></div>
                : null}
              </div>

              { selectors?.list.map((gs, i) =>
                <React.Fragment key={i}>
                  <div className="d-f a-i-s p-v-0.75 b-sb-s b-c-lightestGray b-wb-0.1">
                    <div className="d-f fb-a a-i-s fb-d-c fb-d-r-M">
                      <div className="d-f fb-d-c p-l-0.5 fb-a w-100%">
                        <div className="d-f">
                          <div className="d-f fb-n w-3 a-i-c">
                            <CheckboxWithImage
                              size= {"large"}
                              color={gs.group.color ? gs.group.color : "#4a4a4a"}
                              disabled={!isOwnerOrMember}
                              type={"group"}
                              checked={gs.selected}
                              onChange={isOwnerOrMember ? () => setSelectors(selectors.toggle(gs)) : undefined}
                            />
                          </div>
                          <div className="fb-a d-f a-i-s m-r-1">
                            <div className="max-w-100%">
                              <GroupNameWithColor
                                group={gs.group}
                                textSize={"medium"}
                                link={`/${team.slug}/monitors?groupUuid=${gs.group.uuid}`}/>
                            </div>
                          </div>
                        </div>
                        <div className="p-l-3 p-r-1 p-t-0.5 of-h t-o-e">
                          { gs.group.description }
                        </div>
                        { !gs.expanded && gs.group.monitors?.length > 0 ?
                          <div onClick={() => setSelectors(selectors.expand(gs))} className="d-n-M m-l-3 m-t-0.5 f-s-0.85 t-c-g c-p">{t("Show Monitors")}</div>
                        : null}
                      </div>
                      <div className={`fb-n fb-w-w ${gs.expanded ? 'd-f' : 'd-n'} d-f-M w-20-M w-25-L m-t-0.5 m-t-0.25-M m-l-3.5 m-l-0-M`}>
                        { gs.group.monitors?.length > 0 ?
                          gs.group.monitors.map((m, i) =>
                            <Link to={`/${team.slug}/monitors/${m.uuid}`}
                              key={i}
                              className="d-f fb-n fb-d-r p-r-1.5 max-w-10 t-c-b p-v-0.2">
                              <div className="d-f fb-n a-i-c m-r-0.5">
                                <Status
                                  status={m.status}
                                  transientStatus={m.transientStatus}
                                  type={"monitor"}
                                  size={"mini"} />
                              </div>
                              <div className="fb-a f-s-0.75 ws-n of-h t-o-e" title={m.name}>{ m.name }</div>
                            </Link>)
                        : <span className="t-c-gray f-s-0.75">{t("No monitors")}</span>
                        }
                      </div>
                    </div>
                    {isOwnerOrMember ?
                      <div className="d-f fb-n w-2" title={t("Options")}>
                        <MenuInRow items={[
                          {title: t("Monitors"), link: `/${team.slug}/monitors?groupUuid=${gs.group.uuid}`},
                          { separator: true, },
                          {title: t("Edit"), onClick: () => setEditingSelector(gs.group) },
                          {title: t("Delete"), onClick: () => setRemovingSelector(gs.group)},
                        ]} />
                      </div>
                    : null
                    }
                  </div>
                </React.Fragment>
              )}
            </>
          }

        </div>

        <div className="d-f j-c-c">
          <Paginator
            recordOffset={paginator.recordOffset}
            recordsCount={paginator.recordsCount}
            pageSize={paginator.pageSize}
            url={`${history.location.pathname}${history.location.search}`} />
        </div>

        { editingSelector ?
          <GroupEdit
            groupUuid={editingSelector.uuid}
            onUpdate={onUpdate}
            onCancel={onCancelEdit} />
          : null
        }

        { removingGroup ?
          <Modal
            open={removingGroup !== undefined}
            onClose={ () => setRemovingSelector(undefined) }
            title={t("Delete Group")}
            description={ removingGroup.name }
            secondaryActions={ [ { title: t("Cancel"), onAction: () => setRemovingSelector(undefined) } ] }
            primaryAction={ { title: t("Delete Group"), onAction: execRemove, type: "destructive" } }>
            <div className="p-2">
              <div className="f-s-1.5 f-w-300 t-c-b">
                {t("You can't reverse this action!")}
              </div>
              <div className="f-s-1 f-w-300 m-t-0.5">
                {t("Removing Groups won't remove the grouped monitors.")}
              </div>
            </div>
          </Modal>
          : null
        }

        { isSelectingForRemove ?
          <Modal
            open={isSelectingForRemove}
            onClose={ () => setIsSelectingForRemove(false) }
            title={t("Delete Groups")}
            secondaryActions={ [ { title: t("Cancel"), onAction: () => setIsSelectingForRemove(false) } ] }
            primaryAction={ { title: t("Delete Groups"), onAction: removeGroups, type: "destructive" } }>

            <div className="p-2">
              <div className="f-s-1.5 f-w-300 t-c-b">
                {t("You can't reverse this action!")}
              </div>
              <div className="f-s-1 f-w-300 m-t-0.5">
                {t("Removing Groups won't remove their monitors.")}
              </div>
            </div>
          </Modal>
          : null
        }
      </>
    );
  }
}
